import { createContext, useContext } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import {
  ChatMode,
  ChatPersona,
  ConversationKind,
  HouseholdRespondToChatMessageInput,
  HouseholdSendChatMessageInput,
  MessageResponseAction,
} from '@/types/schema';

import {
  HouseholdChat_MessageFragment,
  SubscribeHouseholdChatSubscription,
} from '../graphql/HouseholdChat.generated';
import { HouseholdChatViewedContext } from '../hooks/useHouseholdChatViewedContext';

export type HouseholdChatStreamEvent =
  SubscribeHouseholdChatSubscription['subscribeHouseholdChat'];

export type HouseholdChatActivity = NonNullable<
  HouseholdChatStreamEvent['activity']
>;

export type HouseholdChatMessage = HouseholdChat_MessageFragment & {
  /**
   * The agent activity trace ("Searching the knowledge graph…") observed while
   * this assistant message was being generated. Only available in-memory for
   * turns streamed during this session; not persisted on the server.
   */
  activities?: HouseholdChatActivity[];
  /** Whether this client-side message is receiving live stream events. */
  isStreaming?: boolean;
  /**
   * The local files attached to an optimistic user message. The transcript
   * shows them until the stored message, with its userUploadedImages,
   * replaces the optimistic one.
   */
  pendingUploadedImages?: File[];
};

export type HouseholdChatView = 'conversation' | 'list';

/** A card response in flight: which message and which action was taken. */
export interface HouseholdChatPendingResponse {
  messageId: string;
  responseAction: MessageResponseAction;
}

export interface HouseholdChatSendOptions {
  referenceImage?: File;
  additionalInput?: Pick<
    HouseholdSendChatMessageInput,
    'diagramContext' | 'withPromptTemplate'
  >;
  onConversationReady?: (conversationId: string) => void;
  onError?: (error: unknown) => void;
}

/**
 * Options for one chat instance that change which messages its transcript
 * shows. The surface that mounts the chat provider sets them.
 */
export interface HouseholdChatTranscriptOptions {
  /**
   * Returns true for a message that the transcript must not show, such as a
   * DIAGRAM_MESSAGE message for the diagram that the surface shows beside the
   * chat. The transcript also hides the rows that it always hides. Keep the
   * function stable (for example, with useCallback): each new function
   * re-renders every chat consumer.
   */
  isMessageHidden?: (message: HouseholdChatMessage) => boolean;
}

export interface HouseholdChatContextValue {
  /** Fetches history and updates the transcript only while this conversation is active. */
  refreshConversation: (
    conversationId: string
  ) => Promise<HouseholdChatMessage[]>;
  awaitingFirstToken: boolean;
  /** True while the conversation is being reconciled after its subscription reconnects. */
  isReconcilingSubscription: boolean;
  /** Answer-depth mode applied to the next message sent in this chat. */
  chatMode: ChatMode;
  /**
   * Whether the Fast/Thinking answer-depth modes are in effect. False when the
   * household_chat_no_modes flag is on: the mode selector is hidden and
   * messages are sent without a mode (the backend runs every turn the same
   * way).
   */
  chatModesEnabled: boolean;
  /**
   * Specialized persona applied to the next message sent in this chat; null
   * for the default (no-persona) chat behavior. Only settable while the
   * persona POC selector is shown.
   */
  chatPersona: ChatPersona | null;
  clearPendingScroll: () => void;
  closeChat: () => void;
  /**
   * Hides the viewed-object context chip (and keeps it out of the next
   * message) until the user navigates to a different object.
   */
  dismissViewedContext: () => void;
  /**
   * True while the current page's viewed-object context is hidden because the
   * user dismissed it — i.e. there is a context that restoreViewedContext
   * would bring back.
   */
  hasDismissedViewedContext: boolean;
  /** Re-shows the viewed-object context chip the user dismissed. */
  restoreViewedContext: () => void;
  /** Server id of the active conversation; null until the first send. */
  conversationId: string | null;
  error: string | null;
  householdId: string;
  isChatOpen: boolean;
  isFullScreenModalHostMounted: boolean;
  /**
   * True when the provider is limited to one conversation. The conversation
   * list and New chat are not available.
   */
  isSingleConversation: boolean;
  /**
   * Returns true for a message that the surface hides from the transcript.
   * Null when the surface hides no messages. See
   * HouseholdChatTranscriptOptions.isMessageHidden.
   */
  isTranscriptMessageHidden:
    | ((message: HouseholdChatMessage) => boolean)
    | null;
  /**
   * Whether the active conversation is one of the household's shared
   * validation-questions conversations, which the user answers through the
   * agent's question cards rather than the composer. False until the kind is
   * known (a new chat, or a selected conversation still loading).
   */
  isValidationQuestionsConversation: boolean;
  lastUserMessage: string | null;
  messages: HouseholdChatMessage[];
  messagesLoading: boolean;
  openChat: () => void;
  /**
   * The interactive message currently blocking the conversation: a message
   * soliciting an in-card response (e.g. a confirmation card) that is
   * PENDING. Null when nothing is awaiting the user's answer.
   */
  pendingInteractiveMessage: HouseholdChatMessage | null;
  pendingScrollToMessageId: string | null;
  /**
   * Increases after a completed chat turn used the presentation edit tool.
   * Presentation surfaces use this value to refresh server-owned content.
   */
  presentationEditCompletedVersion: number;
  /**
   * Whether the household_chat_specialized_persona_poc flag is on: the
   * composer shows the "Workflow" persona controls alongside the
   * answer-depth mode selector. Persona turns ignore mode; no-persona turns
   * keep the normal mode behavior.
   */
  personaSelectorEnabled: boolean;
  registerFullScreenModalHost: () => void;
  /**
   * The card response currently in flight (kept set until the card's status
   * flip arrives from the server); null when none. Drives per-card button
   * loading state and duplicate-click suppression.
   */
  respondingToMessage: HouseholdChatPendingResponse | null;
  /**
   * Routes the user's answer on a pending interactive message to the waiting
   * workflow. The card's status flip and the persisted response row arrive
   * via the stream/refetch, not the mutation response. Never rejects; errors
   * surface through feedback. Returns true when the mutation succeeds and
   * false when it fails or a duplicate submission is ignored.
   */
  respondToMessage: (
    input: HouseholdRespondToChatMessageInput
  ) => Promise<boolean>;
  /**
   * Activates a conversation and loads its history. `knownKind` lets callers
   * that already have the conversation (the list) render kind-dependent chrome
   * immediately instead of after the history loads.
   */
  selectConversation: (
    conversationId: string,
    knownKind?: ConversationKind | null
  ) => void;
  /** Explicit context is used by actions that explain a specific object. */
  sendMessage: (
    message: string,
    contextObjectId?: string,
    options?: HouseholdChatSendOptions
  ) => Promise<HouseholdChatSendResult | null>;
  /** Overrides the answer-depth mode for the next message in this chat. */
  setChatMode: (mode: ChatMode) => void;
  /** Applies (or clears) the specialized persona for the next message. */
  setChatPersona: (persona: ChatPersona | null) => void;
  /** A modal overrides route context; undefined restores route context on exit. */
  setModalViewedContext: (
    context: HouseholdChatViewedContext | null | undefined
  ) => void;
  showConversationList: () => void;
  startNewConversation: () => void;
  streamingMessageId: string | null;
  unregisterFullScreenModalHost: () => void;
  view: HouseholdChatView;
  /**
   * The object the user is currently viewing (document, entity, waterfall,
   * ...), shown as a context chip on the composer and attached to the next
   * message. Null on pages without an attachable object, while its name
   * loads, or after the user dismissed it for the current object.
   */
  viewedContext: HouseholdChatViewedContext | null;
}

export interface HouseholdChatSendResult {
  conversationId: string;
  /**
   * Identifies the exact chat turn started by this send. A conversation can
   * receive later turns, so callers need this stable ID to observe completion
   * without reacting to a different message.
   */
  workflowTrackerId: string;
}

export const HouseholdChatContext = createContext<
  HouseholdChatContextValue | undefined
>(undefined);

export function useHouseholdChatContext() {
  return useGuardedContext(HouseholdChatContext, 'HouseholdChatProvider');
}

export function useOptionalHouseholdChatContext() {
  return useContext(HouseholdChatContext);
}
